import * as vp from 'vopay';
import React, { createContext, ReactNode, useState } from 'react';

export type PartnerPermissions = {
  accountBillingPackageRequired: boolean;
  accountEndpointAccess: boolean;
  accountManagementEnabled: boolean;
  accountOnboardingApiAccess: boolean;
  accountOnboardingFormAccess: boolean;
  customBillingPackageCreationEnabled: boolean;
  leadManagementEnabled: boolean;
  setAccountPermissionsEnabled: boolean;
  transactionEstimatesRequired: boolean;
  CanAddCanadianAccounts: boolean;
  CanAddUSAccounts: boolean;
  PartnerBillsAccount: boolean;
  accountCredentialsAccess: boolean;
  businessCaseRequired: boolean;
  partnerLiability: boolean;
  ShowPartnerIncome: boolean;
  WhitelabellingEnabled: boolean;
  AccountTransfersEnabled: boolean;
  BrandingEmailEnabled: boolean;
  BrandingELinxEnabled: boolean;
  BrandingPortalEnabled: boolean;
  BrandingOnboardingEnabled: boolean;
};

export type PermissionsCtx = {
  permissions: PartnerPermissions;
  setPermissions: (permissions: vp.ApiPartnerPermissions) => void;
  reset: () => void;
};

export const PermissionsContext = createContext<PermissionsCtx>({
  permissions: {
    accountBillingPackageRequired: false,
    accountEndpointAccess: false,
    accountManagementEnabled: false,
    accountOnboardingApiAccess: false,
    accountOnboardingFormAccess: false,
    customBillingPackageCreationEnabled: false,
    leadManagementEnabled: false,
    setAccountPermissionsEnabled: false,
    transactionEstimatesRequired: false,
    CanAddCanadianAccounts: false,
    CanAddUSAccounts: false,
    PartnerBillsAccount: false,
    accountCredentialsAccess: false,
    businessCaseRequired: false,
    partnerLiability: false,
    ShowPartnerIncome: false,
    WhitelabellingEnabled: false,
    AccountTransfersEnabled: false,
    BrandingEmailEnabled: false,
    BrandingELinxEnabled: false,
    BrandingPortalEnabled: false,
    BrandingOnboardingEnabled: false,
  },
  setPermissions: () => {},
  reset: () => {},
});

export const PermissionsProvider = ({ children }: { children: ReactNode }): React.JSX.Element => {
  const [storedPermissions, setStoredPermissions] = useState<vp.ApiPartnerPermissions>();

  const permissions = {
    accountBillingPackageRequired: toBool(storedPermissions?.AccountBillingPackageRequired),
    accountEndpointAccess: toBool(storedPermissions?.AccountEndpointAccess),
    accountManagementEnabled: toBool(storedPermissions?.AccountManagementEnabled),
    accountOnboardingApiAccess: toBool(storedPermissions?.AccountOnboardingApiAccess),
    accountOnboardingFormAccess: toBool(storedPermissions?.AccountOnboardingFormAccess),
    customBillingPackageCreationEnabled: toBool(storedPermissions?.CustomBillingPackageCreationEnabled),
    leadManagementEnabled: toBool(storedPermissions?.LeadManagementEnabled),
    setAccountPermissionsEnabled: toBool(storedPermissions?.SetAccountPermissionsEnabled),
    transactionEstimatesRequired: toBool(storedPermissions?.TransactionEstimatesRequired),
    CanAddCanadianAccounts: toBool(storedPermissions?.CanAddCanadianAccounts),
    CanAddUSAccounts: toBool(storedPermissions?.CanAddUSAccounts),
    PartnerBillsAccount: toBool(storedPermissions?.PartnerBillsAccount),
    accountCredentialsAccess: toBool(storedPermissions?.AccountCredentialsAccess),
    businessCaseRequired: toBool(storedPermissions?.BusinessCaseRequired),
    partnerLiability: toBool(storedPermissions?.PartnerLiability),
    ShowPartnerIncome: toBool(storedPermissions?.ShowPartnerIncome),
    WhitelabellingEnabled: toBool(storedPermissions?.WhitelabellingEnabled),
    AccountTransfersEnabled: toBool(storedPermissions?.AccountTransfersEnabled),
    BrandingEmailEnabled: toBool(storedPermissions?.BrandingEmailEnabled),
    BrandingELinxEnabled: toBool(storedPermissions?.BrandingELinxEnabled),
    BrandingPortalEnabled: toBool(storedPermissions?.BrandingPortalEnabled),
    BrandingOnboardingEnabled: toBool(storedPermissions?.BrandingOnboardingEnabled),
  };

  return (
    <PermissionsContext.Provider
      value={{ permissions, setPermissions: setStoredPermissions, reset: () => setStoredPermissions(undefined) }}
    >
      {children}
    </PermissionsContext.Provider>
  );

  function toBool(val?: string): boolean {
    return Boolean(parseInt(val || '', 2));
  }
};

export const usePermissions = (): PermissionsCtx => React.useContext(PermissionsContext);
